/**
 * #1232 — Polymarket US activity for the board's filters and its operator-risk
 * invariant, from the side rail's own read (#1034): the trader's own open US
 * orders and holdings, the same pooled read the Positions pop-up shows.
 *
 * A US row carries only its market slug. Its game comes from the Tracker's
 * catalogue lookup (`UsMarketClassifier`), which places every market family
 * (moneyline, spread, total, props, period winners), so a period-winner order
 * protects its game's row too. The key is folded exactly as the Tracker's US
 * row link folds it to find the board row (`usGameLink.ts`).
 *
 * Fail-open, as for Kalshi and Polymarket: an empty activity set hides rows
 * only when it is a real answer. Any account the read could not cover, and any
 * exposure whose lookup is pending or failed, keeps every filter off. A market
 * the catalogue answered without (`absent`) is no live US market and protects
 * nothing.
 */
import { foldGameKeySport } from '@/services/analyticsGameKey';
import type { UsLookupAccount, UsMarketLookup } from '@/lib/polymarketUs/marketFamily';
import type { UsRailRead } from './usRailModel';
import type { UsRail } from './useUsRail';

export type UsBoardActivity = {
  /** Folded US canonical game keys with an own open order or a holding. */
  gameKeys: ReadonlySet<string>;
  /** Some exposure's game is not known yet (lookup pending or failed). */
  unresolved: boolean;
};

export const NO_US_ACTIVITY: UsBoardActivity = { gameKeys: new Set(), unresolved: false };

/** The slugs to look up, per account. Rows an account carries from an earlier
 * read (its read failed) are included: they are the best evidence there is. */
export function usExposureLookups(read: UsRailRead | null): { account: UsLookupAccount; slugs: string[] }[] {
  if (!read) return [];
  const out: { account: UsLookupAccount; slugs: string[] }[] = [];
  for (const account of read.accounts) {
    const rows = [...account.orders, ...account.positions];
    const scope = account.scope ?? rows[0]?.scope ?? null;
    if (!scope || rows.length === 0) continue;
    out.push({ account: { accountId: account.accountId, scope }, slugs: [...new Set(rows.map((r) => r.target.marketSlug))] });
  }
  return out;
}

export function usBoardActivity(read: UsRailRead | null, lookup: (slug: string) => UsMarketLookup): UsBoardActivity {
  if (!read) return NO_US_ACTIVITY;
  const gameKeys = new Set<string>();
  let unresolved = false;
  for (const { slugs } of usExposureLookups(read)) {
    for (const slug of slugs) {
      const l = lookup(slug);
      if (l.state === 'placed') {
        if (l.game) gameKeys.add(foldGameKeySport(l.game.gameKey));
      } else if (l.state !== 'absent') {
        unresolved = true;
      }
    }
  }
  return { gameKeys, unresolved };
}

/** Whether "no US activity on this row" can be believed. Only two answers
 * can: a login known to have no US account, or a read that covered every
 * account in full with every exposure placed. `'ready'` alone is not enough:
 * a failed account, unread holdings or unread order pages still publish it. */
export function usActivityIsAuthoritative(
  rail: Pick<UsRail, 'status' | 'hasUs' | 'read'>,
  activity: UsBoardActivity,
): boolean {
  if (rail.status === 'off') return rail.hasUs === false;
  if (rail.status !== 'ready' || !rail.read) return false;
  return !activity.unresolved && rail.read.accounts.every(
    (a) => a.error === null && a.positionsError === null && !a.ordersCapped,
  );
}
